import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
} from "mobx"

import GlobalProjectsCollection from "@src/app/ProjectsCollection"
import { catchErrors, checkoutsApi, projectsApi } from "@src/shared/api"
import {
  CheckoutCreateRequest,
  CheckoutCreateRequestImage,
  CheckoutCreateRequestPricingTypeEnum,
  CheckoutCreateRequestThemeEnum,
  CheckoutResponse,
  CheckoutUpdateRequest,
  CheckoutUpdateRequestThemeEnum,
} from "@src/shared/api/generated"
import { loading } from "@src/shared/classes/utils/loading"
import { ImageUpload } from "@src/shared/componemts/ImageUploadUI/entity/ImageUpload"
import { UploadData } from "@src/shared/form/uploader"

export type CheckoutLogos = {
  original: string
  small: string
}

export type Checkout = {
  description?: string
  fiat_price?: string // fixed price
  logo: UploadData<CheckoutLogos>
  max_contribution_amount?: string
  name?: string
  pricing_type?: "fixed_price" | "no_price"
  project_id?: string
  suggested_amounts?: string[]
}

interface FormData {
  amount: string
  description: string
  image: CheckoutCreateRequestImage
  name: string
  pricing_type: CheckoutCreateRequestPricingTypeEnum
  project_id: string
  return_url: null | string
  symbol: string
  theme: CheckoutCreateRequestThemeEnum & CheckoutUpdateRequestThemeEnum
}

export class CheckoutEntity extends loading(["fetch", "upsert"]) {
  _state: CheckoutResponse = {} as CheckoutResponse

  image = new ImageUpload({ uploadUrl: "/v2.0/checkouts/cache/image" })

  projects = GlobalProjectsCollection()

  constructor() {
    super()

    makeObservable(this, {
      _state: observable.shallow,
      fetch: action.bound,
      id: computed,
      isNew: computed,
      projects: observable,
      toForm: computed,
      update: action.bound,
    })
  }

  async fetch(id: string) {
    try {
      const {
        data: { data: response },
      } = await checkoutsApi.getCheckout(id)

      if (response) {
        const { image = {}, ...state } = response

        // @ts-ignore
        const preview = image.versions?.original

        this._state = state

        if (preview) {
          this.image.setState({ preview })
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }
  }

  async update(data: FormData) {
    const {
      amount,
      pricing_type = CheckoutCreateRequestPricingTypeEnum.FixedPrice,
      symbol,
    } = data

    const info: CheckoutCreateRequest = {
      ...data,
      pricing_type: pricing_type,
      ...(pricing_type === CheckoutCreateRequestPricingTypeEnum.FixedPrice
        ? { fiat_price: { amount, symbol } }
        : {}),
      ...(this.image.cacheId
        ? {
            image: {
              cache_id: this.image.cacheId,
            },
          }
        : {}),
    }

    return this.isNew
      ? checkoutsApi.createCheckout(info)
      : checkoutsApi.updateCheckout(
          this._state.id as string,
          {
            ...info,
            theme: data.theme as CheckoutUpdateRequestThemeEnum,
          } as CheckoutUpdateRequest,
        )
  }

  get id() {
    return this._state.id
  }

  get isNew() {
    return !this.id
  }

  get toForm() {
    return {
      amount: this._state.fiat_price?.amount,
      description: this._state.description,
      fiat_price: this._state.fiat_price?.amount,
      // @ts-ignore
      image: this._state?.image?.versions?.original,
      name: this._state.name,
      pricing_type: this._state.pricing_type ?? "fixed_price",
      project_id: this._state.project_id,
      return_url: this._state.hosted?.return_url,
      symbol: this._state.fiat_price?.symbol,
      theme: this._state.hosted?.theme,
    }
  }
}
